import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    ClickAwayListener,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
    Tooltip,
    Stack,
    Chip,
    Badge,
} from '@mui/material';
import IconButton from 'components/ui/Button/IconButton';
import Transitions from 'components/common/Transitions/Transitions';
import { ThemeMode } from 'types/config';
import { GlobalFilterProps } from 'types/layout';
import { MdFilterAlt } from 'react-icons/md';
import { useGlobalFilterSelection } from 'hooks/useGlobalFilterSelection';
import GlobalFilterContent from './GlobalFilterContent';
import { getValuesNotPresent } from 'utils';
import { isProjectExpired } from 'utils/project';

const GlobalFilter = ({ projects }: GlobalFilterProps) => {
    const {
        selectedGlobalFilterValues,
        setGlobalFilter,
        setGlobalFilters,
        preferencesIsLoading,
        putPreferencesIsLoading,
    } = useGlobalFilterSelection(projects);

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const anchorRef = useRef<any>(null);
    const [open, setOpen] = useState(false);
    const [showExpired, setShowExpired] = useState(false);

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    useEffect(() => {
        !projects && setGlobalFilter('null');

        const valuesNotPresent = getValuesNotPresent(selectedGlobalFilterValues, projects);
        const newSelectedGlobalFiterValues = selectedGlobalFilterValues?.filter(val => !valuesNotPresent.includes(val));

        valuesNotPresent?.length && setGlobalFilters(newSelectedGlobalFiterValues);
    }, [projects, selectedGlobalFilterValues]);

    const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
    const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

    const activeProjects = selectedGlobalFilterValues?.filter(shortName => {
        const project = projects?.find(p => p?.ShortName === shortName);
        return project && !isProjectExpired(project);
    });

    const expiredProjects = selectedGlobalFilterValues?.filter(shortName => {
        const project = projects?.find(p => p?.ShortName === shortName);
        return project && isProjectExpired(project);
    });

    const maxVisibleProjects = isLargeScreen ? 15 : isDesktop ? 10 : matchesXs ? 5 : 7;

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }} id='global-filter-notifications'>
            <Stack direction='row' spacing={1} alignItems='center'>
                <Tooltip title='Project filter'>
                    <Badge
                        badgeContent={selectedGlobalFilterValues?.length || 0}
                        color='success'
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                right: 8,
                                top: 8,
                                fontWeight: 'bold',
                            },
                        }}>
                        <IconButton
                            color='secondary'
                            variant='light'
                            sx={{
                                color: 'text.primary',
                                bgcolor: open ? iconBackColorOpen : iconBackColor,
                                width: 'auto',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.customShadows.z1,
                                },
                            }}
                            ref={anchorRef}
                            onClick={handleToggle}>
                            <MdFilterAlt
                                size={20}
                                color={
                                    !selectedGlobalFilterValues || !(selectedGlobalFilterValues as string[])?.length
                                        ? 'error'
                                        : 'inherit'
                                }
                            />
                            {!isMobile && <Typography sx={{ ml: 1, fontWeight: 500 }}>Projects</Typography>}
                        </IconButton>
                    </Badge>
                </Tooltip>

                {!isMobile ? (
                    selectedGlobalFilterValues && selectedGlobalFilterValues?.length ? (
                        <>
                            {activeProjects?.map(
                                (pr, index) =>
                                    index < maxVisibleProjects && (
                                        <Tooltip title={pr} key={`active-${index}`}>
                                            <Box>
                                                <Chip
                                                    label={pr.length > 20 ? `${pr.slice(0, 20)}...` : pr}
                                                    onDelete={() => pr && setGlobalFilter(pr)}
                                                    variant='filled'
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                        color: theme.palette.primary.main,
                                                        bgcolor: theme.palette.primary.lighter,
                                                        height: 36,
                                                        borderRadius: 1.5,
                                                        '& .MuiChip-label': {
                                                            px: 1.5,
                                                        },
                                                        '& .MuiChip-deleteIcon': {
                                                            color: theme.palette.primary.main,
                                                            fontSize: '1rem',
                                                            '&:hover': {
                                                                color: theme.palette.error.main,
                                                            },
                                                        },
                                                        '&:hover': {
                                                            bgcolor: theme.palette.primary.light,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )
                            )}
                            {expiredProjects?.map(
                                (pr, index) =>
                                    (activeProjects?.length || 0) + index < maxVisibleProjects && (
                                        <Tooltip title={`${pr} (expired)`} key={`expired-${index}`}>
                                            <Box>
                                                <Chip
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <span>{pr.length > 13 ? `${pr.slice(0, 13)}...` : pr}</span>
                                                            <Chip
                                                                label='Expired'
                                                                size='small'
                                                                color='warning'
                                                                sx={{
                                                                    height: 18,
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 600,
                                                                    '& .MuiChip-label': {
                                                                        px: 0.75,
                                                                        py: 0,
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    onDelete={() => pr && setGlobalFilter(pr)}
                                                    variant='filled'
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                        color: theme.palette.text.primary,
                                                        bgcolor: theme.palette.grey[200],
                                                        height: 36,
                                                        borderRadius: 1.5,
                                                        '& .MuiChip-label': {
                                                            px: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        },
                                                        '& .MuiChip-deleteIcon': {
                                                            color: theme.palette.text.secondary,
                                                            fontSize: '1rem',
                                                            '&:hover': {
                                                                color: theme.palette.error.main,
                                                            },
                                                        },
                                                        '&:hover': {
                                                            bgcolor: theme.palette.grey[300],
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )
                            )}
                        </>
                    ) : !selectedGlobalFilterValues || !(selectedGlobalFilterValues as string[])?.length ? (
                        projects?.length ? (
                            <Chip label='No selected projects' color='error' />
                        ) : (
                            <Chip label='No projects available' color='error' />
                        )
                    ) : (
                        <></>
                    )
                ) : (
                    <></>
                )}
            </Stack>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-start'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? -5 : 0, 9],
                            },
                        },
                    ],
                }}>
                {({ TransitionProps }) => (
                    <Transitions
                        type='grow'
                        position={matchesXs ? 'top' : 'top-right'}
                        sx={{ overflow: 'hidden' }}
                        in={open}
                        {...TransitionProps}>
                        <Paper
                            sx={{
                                boxShadow: theme.shadows[8],
                                width: 420,
                                borderRadius: 2,
                                overflow: 'hidden',
                                [theme.breakpoints.down('md')]: {
                                    width: 320,
                                },
                            }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box>
                                    <GlobalFilterContent
                                        projects={projects}
                                        selectedGlobalFilterValues={selectedGlobalFilterValues}
                                        setGlobalFilter={setGlobalFilter}
                                        setGlobalFilters={setGlobalFilters}
                                        putPreferencesIsLoading={putPreferencesIsLoading}
                                        loading={preferencesIsLoading}
                                        showExpired={showExpired}
                                        setShowExpired={setShowExpired}
                                    />
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default GlobalFilter;
